import React, { useState } from 'react';
import { Play, Pause, SkipForward, SkipBack, Volume2, VolumeX, Radio, X, Maximize2, Heart, Ban } from 'lucide-react';
import { useRadio } from '../context/RadioContext';

/**
 * Floating mini-radio player. Persists across the whole site once the user
 * hits Tune In on the main MINDESIGN RADIO section. Shares audio state via
 * RadioContext so playback never stops between page sections.
 */
export const FloatingRadio = () => {
  const {
    current,
    playing,
    muted,
    hasStarted,
    isCurrentLiked,
    togglePlay,
    next,
    prev,
    toggleMute,
    toggleLikeCurrent,
    skipAndRemember,
  } = useRadio();
  const [hidden, setHidden] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Don't render until the visitor has tuned in at least once
  if (!hasStarted || hidden || !current) return null;

  const goToRadio = () => {
    const el = document.getElementById('radio');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div
      className="fixed bottom-5 right-5 z-[60] select-none"
      data-testid="floating-radio"
    >
      {expanded ? (
        <div
          className="bg-gunmetal-900/95 backdrop-blur-md border border-accent-red/40 rounded-lg shadow-2xl shadow-black/60 w-80 p-4 animate-in fade-in slide-in-from-bottom-2"
          data-testid="floating-radio-expanded"
        >
          <div className="flex items-start justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <span
                className={`h-2 w-2 rounded-full ${
                  playing ? 'bg-accent-red animate-pulse' : 'bg-amber-400/60'
                }`}
              />
              <span className="text-[10px] tracking-[0.3em] uppercase text-amber-400/80 font-bold">
                {playing ? 'On Air' : 'Standby'} · MDE
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={goToRadio}
                className="text-steel-400 hover:text-accent-red p-1 transition-colors"
                aria-label="Open Radio section"
                data-testid="floating-radio-jump"
                title="Jump to MINDESIGN RADIO"
              >
                <Maximize2 size={14} />
              </button>
              <button
                type="button"
                onClick={() => setExpanded(false)}
                className="text-steel-400 hover:text-accent-red p-1 transition-colors"
                aria-label="Collapse"
                data-testid="floating-radio-collapse"
              >
                <X size={14} />
              </button>
            </div>
          </div>

          <div className="mb-3">
            <div className="text-sm font-bold text-steel-100 truncate" data-testid="floating-radio-title">
              {current.title}
            </div>
            <div className="text-xs text-steel-400 truncate">
              {current.artist} · {current.duration}
            </div>
          </div>

          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={prev}
              className="bg-gunmetal-800 hover:bg-gunmetal-700 text-steel-200 rounded-md px-2 py-2 transition-colors"
              aria-label="Previous"
              data-testid="floating-radio-prev"
            >
              <SkipBack size={14} />
            </button>
            <button
              type="button"
              onClick={togglePlay}
              className="flex-1 bg-accent-red hover:bg-accent-red-dark text-white rounded-md py-2 font-bold tracking-widest text-xs uppercase transition-colors"
              data-testid="floating-radio-play"
            >
              {playing ? (
                <span className="flex items-center justify-center gap-1.5">
                  <Pause size={14} /> Pause
                </span>
              ) : (
                <span className="flex items-center justify-center gap-1.5">
                  <Play size={14} /> Play
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={next}
              className="bg-gunmetal-800 hover:bg-gunmetal-700 text-steel-200 rounded-md px-2 py-2 transition-colors"
              aria-label="Next"
              data-testid="floating-radio-next"
            >
              <SkipForward size={14} />
            </button>
            <button
              type="button"
              onClick={toggleMute}
              className="bg-gunmetal-800 hover:bg-gunmetal-700 text-steel-200 rounded-md px-2 py-2 transition-colors"
              aria-label={muted ? 'Unmute' : 'Mute'}
              data-testid="floating-radio-mute"
            >
              {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
            </button>
          </div>

          {/* Like + Skip-and-remember row */}
          <div className="mt-2 flex items-center gap-1.5">
            <button
              type="button"
              onClick={toggleLikeCurrent}
              className={`flex-1 flex items-center justify-center gap-1.5 rounded-md py-2 text-[11px] font-bold tracking-widest uppercase transition-all ${
                isCurrentLiked
                  ? 'bg-pink-500/20 text-pink-300 border border-pink-400/40 hover:bg-pink-500/30'
                  : 'bg-gunmetal-800 text-steel-300 hover:bg-gunmetal-700 hover:text-pink-300'
              }`}
              aria-label={isCurrentLiked ? 'Unlike this track' : 'Like this track'}
              data-testid="floating-radio-like"
              title={isCurrentLiked ? 'Liked — tap to remove' : 'Like this track'}
            >
              <Heart size={13} className={isCurrentLiked ? 'fill-pink-300' : ''} />
              {isCurrentLiked ? 'Liked' : 'Like'}
            </button>
            <button
              type="button"
              onClick={skipAndRemember}
              className="flex-1 flex items-center justify-center gap-1.5 rounded-md py-2 text-[11px] font-bold tracking-widest uppercase bg-gunmetal-800 text-steel-300 hover:bg-amber-500/20 hover:text-amber-300 transition-all"
              aria-label="Skip and don't play this again"
              data-testid="floating-radio-skip-remember"
              title="Skip and don't play this again on this device"
            >
              <Ban size={13} />
              Not for me
            </button>
          </div>

          <button
            type="button"
            onClick={() => setHidden(true)}
            className="mt-3 w-full text-[10px] tracking-widest uppercase text-steel-500 hover:text-accent-red transition-colors"
            data-testid="floating-radio-hide"
          >
            Hide for this session
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="group flex items-center gap-2 bg-gunmetal-900/95 backdrop-blur-md border border-accent-red/40 hover:border-accent-red rounded-full pl-2 pr-4 py-2 shadow-2xl shadow-black/60 transition-all hover:shadow-accent-red/30"
          data-testid="floating-radio-pill"
          aria-label={`MINDESIGN Radio — Now playing ${current.title}`}
        >
          <span className="relative flex items-center justify-center w-9 h-9 rounded-full bg-accent-red text-white">
            <Radio size={16} />
            {playing && (
              <span className="absolute inset-0 rounded-full border-2 border-accent-red animate-ping opacity-60"></span>
            )}
          </span>
          <span className="hidden sm:flex flex-col text-left max-w-[160px]">
            <span className="text-[9px] tracking-[0.3em] uppercase text-amber-400/80 font-bold">
              {playing ? 'On Air' : 'Standby'}
            </span>
            <span className="text-xs text-steel-100 font-bold truncate group-hover:text-accent-red transition-colors">
              {current.title}
            </span>
          </span>
        </button>
      )}
    </div>
  );
};

export default FloatingRadio;
